import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { hotelService } from '@/services/hotel.service';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { RoomPublicView } from '@/components/hotel/RoomPublicView';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ChevronRight, BedDouble } from 'lucide-react';

export default function CameraDetail() {
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | notfound

  const load = useCallback(async (soft = false) => {
    if (!soft) setState('loading');
    try {
      const data = await hotelService.publicRoom(id);
      setRoom(data);
      setState('ok');
    } catch (e) {
      if (!soft) setState('notfound');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Refresh periodico: la pagina pubblica riflette le modifiche senza ricaricare.
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <PublicLayout>
      <Seo
        title={room ? `${room.name} — Alloggi` : 'Camera'}
        description={room?.description || 'Scopri la camera e i suoi servizi.'}
        path={`/camera/${id}`}
        image={room?.main_image || undefined}
      />
      <section className="sila-section py-10 sm:py-14" data-testid="camera-detail-page">
        {state === 'loading' && (
          <div className="space-y-6">
            <Skeleton className="aspect-[16/10] w-full rounded-2xl" />
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {state === 'notfound' && (
          <div className="flex flex-col items-center py-24 text-center" data-testid="camera-notfound">
            <BedDouble className="mb-3 h-9 w-9 text-muted-foreground" />
            <h1 className="font-display text-2xl font-bold">Camera non disponibile</h1>
            <p className="mt-2 max-w-md text-muted-foreground">Questa camera non è al momento pubblicata o non esiste più.</p>
            <Button asChild className="mt-6 rounded-full"><Link to="/alloggi">Vedi tutti gli alloggi</Link></Button>
          </div>
        )}

        {state === 'ok' && room && (
          <>
            <nav className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to="/alloggi" className="hover:text-foreground">Alloggi</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="truncate text-foreground">{room.name}</span>
            </nav>
            <RoomPublicView room={room} price={room.price} publicMode bookHref="/contatti" />
          </>
        )}
      </section>
    </PublicLayout>
  );
}
